import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Stack,
  Select,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  Container,
  Fade,
  Zoom,
  Chip
} from '@mui/material';

import {
  MenuBook as PlanIcon,
  School as LessonIcon,
  Assignment as WorksheetIcon,
  Star as RubricIcon,
  EmojiEvents as ActivityIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';

import UnitLessonPlanGenerator from './UnitLessonPlanGenerator';
import UnitPlanGenerator from './UnitPlanGenerator';
import WorksheetGenerator from './WorksheetGenerator';
import RubricGenerator from './RubricGenerator';
import ActivityGenerator from './ActivityGenerator';
import YouTubeSummaryGenerator from './YouTubeSummaryGenerator';
const TeacherResources = () => {
  const [activeTab, setActiveTab] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const resources = [
    {
      index: 0,
      title: '📘 Unit Plan Generator',
      label: 'Unit Plan Generator',
      description: 'Create detailed unit plans aligned with BC curriculum',
      icon: <PlanIcon />,
      component: <UnitPlanGenerator />,
      color: theme.palette.primary.main,
      text: 'Create Unit Plan'
    },
    {
      index: 1,
      title: '📗 Lesson Plan Generator',
      label: 'Lesson Plan Generator',
      description: 'Design engaging, structured lesson plans quickly',
      icon: <LessonIcon />,
      component: <UnitLessonPlanGenerator />,
      color: theme.palette.success.main,
      text: 'Develop Lesson Plans'
    },
    {
      index: 2,
      title: '📙 Worksheet Generator',
      label: 'Worksheet Generator',
      description: 'Generate printable worksheets by DOK level',
      icon: <WorksheetIcon />,
      component: <WorksheetGenerator />,
      color: theme.palette.warning.main,

      text: 'Make Worksheets'
    },
    {
      index: 3,
      title: '📒 Rubric Generator',
      label: 'Rubric Generator',
      description: 'Create customized rubrics for assessments',
      icon: <RubricIcon />,
      component: <RubricGenerator />,
      color: theme.palette.secondary.main,
      text: 'Create Rubric'
    },
    {
      index: 4,
      title: '📘 Activity Generator',
      label: 'Activity Generator',
      description: 'Design engaging learning activities',
      icon: <ActivityIcon />,
      component: <ActivityGenerator />,
      color: theme.palette.error.main,
      text: 'Develop Activities'
    },

    {
      index: 5,
      title: '📘 Video Summarizer',
      label: 'Youtube Video Summary',
      description: 'Generate summary and discussion questions from a YoutTube video',
      icon: <ActivityIcon />,
      component: <YouTubeSummaryGenerator />,
      color: theme.palette.error.main,
      text: 'YouTube Education'
    }
  ];

  const handleBackToMenu = () => {
    setActiveTab(null);
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      {/* Header */}


      {/* Back Button */}
      {activeTab !== null && (
        <Box sx={{ mb: 3, px: { xs: 1, sm: 2 } }}>
          <Chip
            icon={<BackIcon />}
            label="Back"
            onClick={handleBackToMenu}
            variant="outlined"
            color="primary"
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: theme.palette.primary.light,
                color: theme.palette.primary.contrastText
              }
            }}
          />
        </Box>
      )}

      {/* Menu */}
      {activeTab === null ? (
        <Fade in timeout={600}>
          <Box>
            {isMobile ? (
              <Stack spacing={2} sx={{ px: { xs: 1, sm: 2 } }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{
                    color: theme.palette.primary.dark,
                    textAlign: "center",
                    mb: 2
                  }}
                >
                  Select Tool
                </Typography>
                <Select
                  fullWidth
                  value=""
                  onChange={(e) => setActiveTab(e.target.value)}
                  displayEmpty
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 3,
                    mb: 3,
                    boxShadow: 2,
                    '& .MuiSelect-select': {
                      py: 2,
                      px: 3,
                      fontSize: '1rem'
                    },
                    '&:hover': {
                      boxShadow: 4
                    }
                  }}
                >
                  <MenuItem value="" disabled>
                    <Typography variant="body1" color="text.secondary">
                      Choose a tool to get started...
                    </Typography>
                  </MenuItem>
                  {resources.map((res) => (
                    <MenuItem key={res.index} value={res.index}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Box sx={{
                          p: 1,
                          borderRadius: '50%',
                          backgroundColor: `${res.color}20`,
                          color: res.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {res.icon}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" fontWeight="600">
                            {res.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {res.description}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
            ) : (
              <Grid container spacing={3} sx={{ px: { xs: 1, sm: 2 } }}>
                {resources.map((res, index) => (
                  <Grid item xs={12} sm={6} md={4} key={res.index}>
                    <Zoom in timeout={600 + index * 100}>
                      <Card
                        elevation={3}
                        onClick={() => setActiveTab(res.index)}
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          borderRadius: 4,
                          border: `2px solid transparent`,
                          background: theme.palette.background.paper,
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: 8,
                            border: `2px solid ${res.color}`,
                            '& .resource-icon': {
                              transform: 'scale(1.1)',
                              backgroundColor: `${res.color}20`
                            }
                          },
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <CardContent sx={{
                          textAlign: 'center',
                          p: { xs: 2, sm: 3 },
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between'
                        }}>
                          <Box>
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              mb: 2
                            }}>
                              <Box
                                className="resource-icon"
                                sx={{
                                  p: 2,
                                  borderRadius: '50%',
                                  backgroundColor: `${res.color}15`,
                                  color: res.color,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.3s ease',
                                  fontSize: '2rem'
                                }}
                              >
                                {res.icon}
                              </Box>
                            </Box>
                            <Typography
                              variant="h6"
                              fontWeight="bold"
                              sx={{ mb: 1, color: theme.palette.text.primary }}
                            >
                              {res.label}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ lineHeight: 1.6 }}
                            >
                              {res.description}
                            </Typography>
                          </Box>
                          <Chip
                            label={res.text}
                            color="primary"
                            variant="outlined"
                            size="small"
                            sx={{
                              mt: 2,
                              alignSelf: 'center',
                              '&:hover': {
                                backgroundColor: res.color,
                                color: 'white'
                              }
                            }}
                          />
                        </CardContent>
                      </Card>
                    </Zoom>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Fade>
      ) : (
        /* Component Renderer */
        <Fade in timeout={400}>
          <Paper
            elevation={4}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              borderRadius: 4,
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            {resources[activeTab].component}
          </Paper>
        </Fade>
      )}
    </Container>
  );
};

export default TeacherResources;
